import React, { useState, useEffect, useRef } from 'react';
import { User, Bot } from 'lucide-react';
import './SimulatedChatDemo.css';

const SimulatedChatDemoNFL = ({ handleSignUp }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);

  // NFL Buccaneers vs Lions MNF - Player Props showcase
  const chatScript = [
    {
      type: 'user',
      content: 'buccaneers vs lions MNF, bet player props',
      delay: 1200
    },
    {
      type: 'bot',
      content: 'Analyzing Buccaneers vs Lions player props...',
      typing: true,
      delay: 1800
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-3">
          <div className="text-sm text-white/70">
            <span className="text-[10px] font-medium tracking-[0.2em] text-emerald-400/70 uppercase block mb-2">
              🏈 Amon-Ra St. Brown - Receiving (Lions WR)
            </span>
            <div className="bg-black/30 border border-white/[0.03] rounded p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50">Anytime TD</span>
                <span className="font-mono text-emerald-400/80">+95 to +120</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50">Rec Yards Line</span>
                <span className="font-mono text-emerald-400/80">OVER 63.5 yds</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50">AI Projection</span>
                <span className="font-mono text-violet-400/70">82+ yards</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50">Target Share</span>
                <span className="font-mono text-emerald-400/70">No. 1 WR target</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-white/50">
            ✅ St. Brown is Goff's primary target. <span className="text-emerald-400/60">High confidence! 🔥</span>
          </p>
        </div>
      ),
      delay: 2400
    },
    {
      type: 'user',
      content: 'What about Mike Evans?',
      delay: 3000
    },
    {
      type: 'bot',
      content: 'Analyzing Mike Evans TD odds & red zone stats...',
      typing: true,
      delay: 1200
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border border-emerald-500/10 rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-medium text-white/70">🎯 Mike Evans - Anytime TD (Bucs WR)</span>
              <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400/80 rounded font-mono">
                RED ZONE THREAT ✅
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[10px]">
              <div>
                <span className="text-white/30 block">Anytime TD Odds</span>
                <span className="font-mono text-white/70">+115 to +140</span>
              </div>
              <div>
                <span className="text-white/30 block">Yardage Line</span>
                <span className="font-mono text-white/70">OVER 60.5 yds</span>
              </div>
              <div>
                <span className="text-white/30 block">🎯 Red Zone Target</span>
                <span className="font-mono text-emerald-400/70">Mayfield's #1</span>
              </div>
              <div>
                <span className="text-white/30 block">Form</span>
                <span className="font-mono text-emerald-400/70">Red hot 🔥</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-white/[0.05]">
              <span className="text-[10px] text-white/40">💡 Evans dominates in end zone </span>
              <span className="font-mono text-xs text-emerald-400/80">Medium-High confidence</span>
            </div>
          </div>
        </div>
      ),
      delay: 2400
    },
    {
      type: 'user',
      content: 'Add Jahmyr Gibbs rushing',
      delay: 3000
    },
    {
      type: 'bot',
      content: 'Checking Gibbs rushing yards vs Bucs run defense...',
      typing: true,
      delay: 1200
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-3">
          <div className="bg-black/30 border border-white/[0.03] rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-medium text-white/70">🦁 Jahmyr Gibbs - Rushing (Lions RB)</span>
              <span className="text-[10px] px-2 py-0.5 bg-violet-500/20 text-violet-400/80 rounded font-mono">
                SPEED BACK
              </span>
            </div>
            <div className="space-y-2 text-[10px]">
              <div className="flex justify-between">
                <span className="text-white/40">Line</span>
                <span className="font-mono text-white/70">OVER 58.5 yards</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Anytime TD Odds</span>
                <span className="font-mono text-emerald-400/70">-110</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">🛡️ Bucs Run Defense</span>
                <span className="font-mono text-red-400/70">Vulnerable to speed</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">🏃 Usage</span>
                <span className="font-mono text-emerald-400/70">Detroit's chunk yardage</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-white/[0.05] text-[10px] text-white/50">
              ✅ Detroit leans on Gibbs. Bucs struggle vs speed. Medium confidence!
            </div>
          </div>
        </div>
      ),
      delay: 2400
    },
    {
      type: 'user',
      content: 'Any other edges for this game?',
      delay: 3000
    },
    {
      type: 'bot',
      content: 'Finding additional value plays...',
      typing: true,
      delay: 1200
    },
    {
      type: 'bot',
      content: (
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-emerald-500/[0.05] to-violet-500/[0.05] border border-emerald-500/20 rounded p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-medium text-white/80">🎯 Best Actionable Picks</span>
              <span className="text-[10px] px-2 py-0.5 bg-violet-500/30 text-violet-400 rounded font-mono">
                MNF SPECIAL
              </span>
            </div>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex items-center justify-between bg-black/20 p-2 rounded">
                <span className="text-white/60">St. Brown OVER 63.5 rec yds</span>
                <span className="font-mono text-emerald-400/70">HIGH ⭐</span>
              </div>
              <div className="flex items-center justify-between bg-black/20 p-2 rounded">
                <span className="text-white/60">Mayfield UNDER 227.5 yds</span>
                <span className="font-mono text-emerald-400/70">MED-HIGH</span>
              </div>
              <div className="flex items-center justify-between bg-black/20 p-2 rounded">
                <span className="text-white/60">Mike Evans Anytime TD</span>
                <span className="font-mono text-violet-400/70">+130</span>
              </div>
              <div className="flex items-center justify-between bg-black/20 p-2 rounded">
                <span className="text-white/60">Gibbs OVER 58.5 rush yds</span>
                <span className="font-mono text-emerald-400/70">MEDIUM</span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/[0.05] flex justify-between items-center">
              <span className="text-[10px] text-white/40">🎲 Longshots</span>
              <span className="font-mono text-[10px] text-violet-400/70">Otton TD +340, LaPorta TD +190</span>
            </div>
          </div>
          <p className="text-xs text-white/50">
            ✅ Most confident: St. Brown receiving yards. Mayfield passing under has analytical value. 📊
          </p>
        </div>
      ),
      delay: 2400
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    if (scrollRef.current && containerRef.current) {
      const scrollHeight = scrollRef.current.scrollHeight;
      const containerHeight = containerRef.current.clientHeight;

      // Only scroll if content exceeds container
      if (scrollHeight > containerHeight) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
  }, [messages]);

  // Message typing effect
  useEffect(() => {
    if (currentMessageIndex < chatScript.length) {
      const currentMessage = chatScript[currentMessageIndex];
      const timer = setTimeout(() => {
        if (currentMessage.typing) {
          // Show typing indicator
          setMessages(prev => [...prev, { ...currentMessage, isTyping: true }]);

          // Replace with actual message after short delay
          setTimeout(() => {
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                ...chatScript[currentMessageIndex + 1],
                isTyping: false
              };
              return newMessages;
            });
            setCurrentMessageIndex(currentMessageIndex + 2);
          }, 1000);
        } else {
          setMessages(prev => [...prev, currentMessage]);
          setCurrentMessageIndex(currentMessageIndex + 1);
        }
      }, currentMessage.delay);

      return () => clearTimeout(timer);
    } else {
      // Reset and loop the conversation
      const resetTimer = setTimeout(() => {
        setMessages([]);
        setCurrentMessageIndex(0);
      }, 5000);

      return () => clearTimeout(resetTimer);
    }
  }, [currentMessageIndex]);

  return (
    <div
      ref={containerRef}
      className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg overflow-hidden"
      style={{ height: '420px' }}
    >
      {/* Chat header */}
      <div className="absolute top-0 left-0 right-0 bg-black/50 backdrop-blur-sm border-b border-white/[0.05] px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500/70 rounded-full animate-pulse"></span>
            <span className="text-[11px] font-medium tracking-[0.15em] text-white/30 uppercase">
              Watch us build your next win
            </span>
          </div>
          <span className="text-[10px] text-white/20 font-mono">
            Auto-replay
          </span>
        </div>
      </div>

      {/* Messages container */}
      <div
        ref={scrollRef}
        className="h-full pt-14 pb-4 px-4 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10"
        style={{
          scrollBehavior: 'smooth',
          maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
        }}
      >
        <div className="space-y-4 py-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}
                         animate-fadeIn`}
            >
              {message.type === 'bot' && (
                <div className="flex-shrink-0 w-7 h-7 rounded bg-gradient-to-br from-emerald-500/10 to-violet-500/10 border border-white/[0.05] flex items-center justify-center">
                  <Bot className="w-4 h-4 text-emerald-400/70" />
                </div>
              )}

              <div className={`max-w-[280px] ${message.type === 'user' ? 'order-first' : ''}`}>
                <div
                  className={`
                    ${message.type === 'user'
                      ? 'bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border-emerald-500/20 text-white/80'
                      : 'bg-white/[0.02] border-white/[0.05] text-white/70'
                    }
                    border backdrop-blur-sm rounded-lg px-3 py-2 text-xs
                  `}
                >
                  {message.isTyping ? (
                    <div className="flex gap-1 py-1">
                      <span className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce ml-1" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce ml-1" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  ) : (
                    message.content
                  )}
                </div>
              </div>

              {message.type === 'user' && (
                <div className="flex-shrink-0 w-7 h-7 rounded bg-white/[0.05] border border-white/[0.05] flex items-center justify-center">
                  <User className="w-4 h-4 text-white/50" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Overlay - appears after first few messages */}
      {messages.length >= 4 && (
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleSignUp}
            className="w-full px-6 py-3 border border-violet-500/30 text-[11px] text-white/70
                     hover:text-violet-300/80 hover:border-violet-400/50 transition-all
                     bg-black/50 backdrop-blur-sm uppercase tracking-[0.15em]"
          >
            Try Now
          </button>
        </div>
      )}
    </div>
  );
};

export default SimulatedChatDemoNFL;